import { useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useAuth } from '@shop/api-client'
import { BRAND, formatMoney, toNumber } from '@shop/shared'
import { Banner, Empty } from '@shop/ui'
import { Keypad, Sheet, TouchButton } from '../pos/touch'
import { PosPerm } from '../pos/contracts'
import { enqueue, useQueue } from './queue'
import { keys, read, subscribe, write } from './store'
import type { CatalogItem, LocalShift, Payee, Station } from './store'

/**
 * الصندوقُ دون اتّصال — <b>طلبُ المالك ٢٠٢٦-١٠-١٠</b> (`0052`).
 *
 * يبيع من نسخة الجهاز، ويفتح الورديّةَ ويُغلقها، ويُخرج المصروف، ويسجّل الدوام —
 * وكلُّ ذلك عمليّاتٌ تُحفظ على الجهاز وتُرحَّل حين يعود النت، بلحظتها وسعرها.
 *
 * ⚠ **وأبسطُ من الصندوق المتّصل عمدًا**: لا مرتجعَ (يحتاج الفاتورةَ الأصليّة من
 *   الخادم)، ولا آجلَ (يحتاج سقفَ الزبون)، ولا خصمَ ولا تعديلَ سعر — السعرُ ما في
 *   النسخة. وما لا يعمل فاصلًا لا يُعرض، بدلَ أن يُعرض ويفشل.
 */
export default function OfflineCounter() {
  const { session } = useAuth()
  const me = session!.me
  const userId = me.userId ?? ''

  useSyncExternalStore(subscribe, () => localStorage.getItem('pos:offline:shift') ?? '')

  const station = read<Station | null>(keys.station, null)
  const shift = read<LocalShift | null>(keys.shift, null)
  const { pending, failed } = useQueue()

  const [view, setView] = useState<'sell' | 'payout' | 'punch' | 'close' | 'queue'>('sell')

  const can = (code: string) => me.permissions.includes(code)

  if (!station) {
    return (
      <Shell pending={pending.length} failed={failed.length} onQueue={() => setView('queue')}>
        <Banner tone="warn">
          النت مقطوع، وهذا الجهاز لم يحفظ نسخةً من الأصناف بعد. أوّلُ مرّةٍ تحتاج اتّصالًا.
        </Banner>
      </Shell>
    )
  }

  const mine = shift && shift.userId === userId ? shift : null

  if (!mine) {
    return (
      <Shell pending={pending.length} failed={failed.length} onQueue={() => setView('queue')}>
        {can(PosPerm.shiftOpen) ? (
          <OpenOffline station={station} userId={userId} />
        ) : (
          <Banner tone="warn">لا ورديّةَ مفتوحةٌ لك، ولا تملك صلاحيّةَ فتحها.</Banner>
        )}
        {view === 'queue' && <QueueSheet onClose={() => setView('sell')} />}
      </Shell>
    )
  }

  return (
    <Shell
      pending={pending.length}
      failed={failed.length}
      onQueue={() => setView('queue')}
      actions={
        <>
          {can(PosPerm.cashPayout) && <HeaderButton label="مصروف" onClick={() => setView('payout')} />}
          {can(PosPerm.attendancePunch) && <HeaderButton label="الدوام" onClick={() => setView('punch')} />}
          {can(PosPerm.shiftClose) && <HeaderButton label="إغلاق" onClick={() => setView('close')} />}
        </>
      }
      subtitle={`${mine.sessionNo} · ${me.username}`}
    >
      <SellOffline station={station} shift={mine} userId={userId} />

      {view === 'payout' && <PayoutSheet shift={mine} userId={userId} onClose={() => setView('sell')} />}
      {view === 'punch' && <PunchSheet userId={userId} onClose={() => setView('sell')} />}
      {view === 'close' && <CloseSheet shift={mine} userId={userId} onClose={() => setView('sell')} />}
      {view === 'queue' && <QueueSheet onClose={() => setView('sell')} />}
    </Shell>
  )
}

// ── الإطار ──────────────────────────────────────────────────────────────────

function Shell({
  children, actions, subtitle, pending, failed, onQueue,
}: {
  children: React.ReactNode
  actions?: React.ReactNode
  subtitle?: string
  pending: number
  failed: number
  onQueue: () => void
}) {
  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-10 flex min-h-14 items-center justify-between gap-2 border-b border-line bg-surface px-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold">{BRAND.nameAr}</p>
          {subtitle && <p className="tabular truncate text-xs text-ink-soft" dir="ltr">{subtitle}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-1">{actions}</div>
      </header>

      <button
        type="button"
        onClick={onQueue}
        className="flex w-full items-center justify-between gap-2 border-b border-warn/40 bg-warn/10 px-3 py-2 text-sm"
      >
        <span>
          <b>دون اتّصال</b> — البيعُ يُحفظ على الجهاز ويُرحَّل حين يرجع النت.
        </span>
        <span className="tabular shrink-0 text-xs">
          {pending} تنتظر{failed > 0 && <b className="text-danger"> · {failed} لم تُرحَّل</b>}
        </span>
      </button>

      <main className="mx-auto max-w-3xl p-3">{children}</main>
    </div>
  )
}

function HeaderButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="min-h-11 rounded-lg px-3 text-sm font-semibold hover:bg-surface-raised">
      {label}
    </button>
  )
}

// ── فتحُ الورديّة ───────────────────────────────────────────────────────────

function OpenOffline({ station, userId }: { station: Station; userId: string }) {
  const [amount, setAmount] = useState('')

  const open = () => {
    const opening = toNumber(amount) ?? 0

    const op = enqueue({
      kind: 'SHIFT_OPEN',
      userId,
      payload: { branchId: station.branchId, openingCash: opening },
      summary: `فتحُ ورديّة بـ ${formatMoney(opening)}`,
    })

    write(keys.shift, {
      serverId: null,
      clientId: op.clientId,
      sessionNo: 'ورديّةٌ دون اتّصال',
      userId,
      openedAt: op.occurredAt,
      openingCash: opening,
    } satisfies LocalShift)
  }

  return (
    <div className="mx-auto grid max-w-sm gap-3">
      <h2 className="text-lg font-bold">افتح ورديّة</h2>
      <p className="text-sm text-ink-soft">كم في الدرج الآن؟</p>
      <div className="tabular rounded-lg border border-line bg-surface px-3 py-2 text-left text-2xl font-bold" dir="ltr">
        {amount || '0'}
      </div>
      <Keypad value={amount} onChange={setAmount} />
      <TouchButton tone="primary" wide onClick={open}>افتح الورديّة</TouchButton>
    </div>
  )
}

// ── البيع ───────────────────────────────────────────────────────────────────

/** توحيدُ النصّ للبحث — <b>كـ<c>ArabicText.Normalize</c> في الخادم</b>: «أنكر» = «انكر». */
function normalize(text: string): string {
  return text
    .normalize('NFKC')
    .replace(/[ً-ْـٰ]/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/[ىئ]/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/ؤ/g, 'و')
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .toLowerCase()
    .trim()
}

type CartLine = { item: CatalogItem; qty: number }

type Method = 'CASH' | 'CARD' | 'CLIQ'

const METHOD_LABEL: Record<Method, string> = { CASH: 'نقدًا', CARD: 'بطاقة', CLIQ: 'كليك' }

function SellOffline({ station, shift, userId }: { station: Station; shift: LocalShift; userId: string }) {
  const catalog = useMemo(() => read<CatalogItem[]>(keys.catalog(station.warehouseId), []), [station.warehouseId])
  const savedAt = read<string | null>(keys.catalogAt(station.warehouseId), null)

  const [query, setQuery] = useState('')
  const [cart, setCart] = useState<CartLine[]>([])
  const [paying, setPaying] = useState(false)
  const [done, setDone] = useState<{ lines: CartLine[]; total: number; method: Method; at: string } | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  const total = cart.reduce((sum, line) => sum + Math.round((line.item.price ?? 0) * line.qty * 1000) / 1000, 0)

  const hits = useMemo(() => {
    const needle = normalize(query)

    if (needle.length < 2) {
      return []
    }

    return catalog
      .filter((item) =>
        normalize(item.productName).includes(needle)
        || item.sku.toLowerCase() === needle
        || item.barcodes.includes(query.trim()))
      .slice(0, 20)
  }, [catalog, query])

  const add = (item: CatalogItem) => {
    if (item.price === null) {
      setNotice(`«${item.productName}» بلا سعرٍ في النسخة — لا يُباع وهو فاصل.`)
      return
    }

    setNotice(null)
    setCart((lines) => {
      const found = lines.find((l) => l.item.variantId === item.variantId)

      return found
        ? lines.map((l) => (l === found ? { ...l, qty: l.qty + 1 } : l))
        : [...lines, { item, qty: 1 }]
    })
    setQuery('')
    input.current?.focus()
  }

  // ⚠ **والمسحُ مطابقةٌ تامّة**: الماسحُ يكتب الباركودَ ثمّ Enter.
  const scan = () => {
    const code = query.trim()
    const exact = catalog.find((item) => item.barcodes.includes(code) || item.sku === code)

    if (exact) {
      add(exact)
    } else if (hits.length === 1) {
      add(hits[0])
    }
  }

  const sold = (method: Method, tendered: number | null, reference: string | null) => {
    const lines = cart.map((line) => ({
      variantId: line.item.variantId,
      qty: line.qty,
      unitPrice: line.item.price,
    }))

    const amount = Math.round(total * 1000) / 1000

    enqueue({
      kind: 'SALE',
      userId,
      payload: {
        session: shift.serverId ? { serverId: shift.serverId } : { clientId: shift.clientId },
        branchId: station.branchId,
        warehouseId: station.warehouseId,
        lines,
        payments: [{
          methodCode: method,
          amount,
          tendered: method === 'CASH' && tendered && tendered > amount ? tendered : null,
          referenceNo: reference,
        }],
      },
      summary: `بيعة ${formatMoney(amount)} ${METHOD_LABEL[method]} — ${cart.length} صنف`,
      cash: method === 'CASH' ? amount : 0,
    })

    setDone({ lines: cart, total: amount, method, at: new Date().toLocaleString('ar-JO-u-nu-latn') })
    setCart([])
    setPaying(false)
  }

  return (
    <div className="grid gap-3">
      <input
        ref={input}
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            scan()
          }
        }}
        placeholder="امسح الباركود أو اكتب اسم الصنف"
        className="min-h-14 w-full rounded-xl border border-line bg-surface px-4 text-lg"
      />

      {savedAt && (
        <p className="text-xs text-ink-soft">
          الأصنافُ والأسعارُ من نسخة {new Date(savedAt).toLocaleString('ar-JO-u-nu-latn')} — {catalog.length} صنفًا.
        </p>
      )}

      {notice && <Banner tone="warn">{notice}</Banner>}

      {hits.length > 0 && (
        <ul className="grid gap-1 rounded-xl border border-line p-1">
          {hits.map((item) => (
            <li key={item.variantId}>
              <button
                type="button"
                onClick={() => add(item)}
                className="flex w-full items-baseline gap-2 rounded-lg px-3 py-2 text-start hover:bg-surface-raised"
              >
                <span className="min-w-0 flex-1 truncate">
                  {item.productName}
                  {item.label && <span className="text-xs text-ink-soft"> · {item.label}</span>}
                </span>
                <span className="tabular shrink-0 font-bold" dir="ltr">
                  {item.price === null ? '—' : formatMoney(item.price)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {cart.length === 0 ? (
        <Empty>السلّةُ فارغة.</Empty>
      ) : (
        <ul className="grid gap-1">
          {cart.map((line) => (
            <li key={line.item.variantId} className="flex items-center gap-2 rounded-lg border border-line px-3 py-2">
              <span className="min-w-0 flex-1 truncate">{line.item.productName}</span>
              <button
                type="button"
                className="min-h-11 min-w-11 rounded-lg border border-line"
                onClick={() => setCart((lines) => lines
                  .map((l) => (l === line ? { ...l, qty: l.qty - 1 } : l))
                  .filter((l) => l.qty > 0))}
              >
                −
              </button>
              <span className="tabular w-8 text-center" dir="ltr">{line.qty}</span>
              <button
                type="button"
                className="min-h-11 min-w-11 rounded-lg border border-line"
                onClick={() => setCart((lines) => lines.map((l) => (l === line ? { ...l, qty: l.qty + 1 } : l)))}
              >
                +
              </button>
              <span className="tabular w-20 shrink-0 text-end font-bold" dir="ltr">
                {formatMoney((line.item.price ?? 0) * line.qty)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between gap-3 rounded-xl bg-surface-raised p-3">
        <span className="text-sm">المجموع</span>
        <b className="tabular text-2xl" dir="ltr">{formatMoney(total)}</b>
      </div>

      <TouchButton tone="primary" wide disabled={cart.length === 0} onClick={() => setPaying(true)}>
        ادفع
      </TouchButton>

      {paying && <PaySheet total={total} onClose={() => setPaying(false)} onPaid={sold} />}
      {done && <ReceiptSheet sale={done} onClose={() => { setDone(null); input.current?.focus() }} />}
    </div>
  )
}

function PaySheet({
  total, onClose, onPaid,
}: {
  total: number
  onClose: () => void
  onPaid: (method: Method, tendered: number | null, reference: string | null) => void
}) {
  const [method, setMethod] = useState<Method>('CASH')
  const [tendered, setTendered] = useState('')
  const [reference, setReference] = useState('')

  const given = toNumber(tendered)
  const change = method === 'CASH' && given !== null && given > total ? given - total : 0

  // ⚠ **والبطاقةُ تطلب رقمَ الموافقة** — كما متّصلةً (`requires_reference`).
  const ready = method !== 'CARD' || reference.trim().length > 0

  return (
    <Sheet title={`ادفع ${formatMoney(total)}`} onClose={onClose}>
      <div className="mx-auto grid max-w-md gap-3">
        <div className="grid grid-cols-3 gap-2">
          {(['CASH', 'CARD', 'CLIQ'] as Method[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMethod(m)}
              className={`min-h-14 rounded-xl border text-base font-semibold ${
                method === m ? 'border-brand bg-brand/10 text-brand' : 'border-line bg-surface'
              }`}
            >
              {METHOD_LABEL[m]}
            </button>
          ))}
        </div>

        {method === 'CASH' && (
          <>
            <p className="text-sm text-ink-soft">كم أعطاك الزبون؟ (اختياريّ — للفكّة)</p>
            <div className="tabular rounded-lg border border-line bg-surface px-3 py-2 text-left text-2xl font-bold" dir="ltr">
              {tendered || '0'}
            </div>
            <Keypad value={tendered} onChange={setTendered} />
            {change > 0 && <Banner tone="ok">الباقي للزبون: {formatMoney(change)}</Banner>}
          </>
        )}

        {method !== 'CASH' && (
          <input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder={method === 'CARD' ? 'رقمُ الموافقة من الماكنة (إلزاميّ)' : 'رقمُ حركة كليك (اختياريّ)'}
            className="min-h-12 rounded-lg border border-line bg-surface px-3"
          />
        )}

        <TouchButton
          tone="primary"
          wide
          disabled={!ready}
          onClick={() => onPaid(method, given, reference.trim() || null)}
        >
          تمّ البيع
        </TouchButton>
      </div>
    </Sheet>
  )
}

function ReceiptSheet({
  sale, onClose,
}: {
  sale: { lines: CartLine[]; total: number; method: Method; at: string }
  onClose: () => void
}) {
  return (
    <Sheet title="حُفظت البيعة" onClose={onClose}>
      <div className="mx-auto grid max-w-sm gap-3">
        <div className="rounded-xl border border-line bg-surface-raised p-4 print:border-0" id="receipt">
          <p className="text-center font-bold">{BRAND.nameAr}</p>
          <p className="tabular text-center text-xs" dir="ltr">{sale.at}</p>
          <ul className="mt-3 grid gap-1 text-sm">
            {sale.lines.map((line) => (
              <li key={line.item.variantId} className="flex justify-between gap-2">
                <span>{line.item.productName} ×{line.qty}</span>
                <span className="tabular" dir="ltr">{formatMoney((line.item.price ?? 0) * line.qty)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 flex justify-between border-t border-line pt-2 font-bold">
            <span>المجموع ({METHOD_LABEL[sale.method]})</span>
            <span className="tabular" dir="ltr">{formatMoney(sale.total)}</span>
          </p>
          <p className="mt-2 text-center text-xs text-ink-soft">رقمُ الفاتورة يصدر حين يرجع النت.</p>
        </div>

        <div className="grid grid-cols-2 gap-2 print:hidden">
          <TouchButton onClick={() => window.print()}>اطبع</TouchButton>
          <TouchButton tone="primary" onClick={onClose}>بيعةٌ جديدة</TouchButton>
        </div>
      </div>
    </Sheet>
  )
}

// ── المصروف ─────────────────────────────────────────────────────────────────

const PAYOUT_CATEGORIES = [
  { id: 'EMPLOYEE', label: 'موظّف' },
  { id: 'SUPPLIER', label: 'مورّد' },
  { id: 'SHOP', label: 'مصاريف المحلّ' },
  { id: 'INTERNET', label: 'إنترنت وهاتف' },
  { id: 'JAMIYA', label: 'الجمعيّة' },
  { id: 'OTHER', label: 'أخرى' },
] as const

function PayoutSheet({ shift, userId, onClose }: { shift: LocalShift; userId: string; onClose: () => void }) {
  const options = read<{ employees: Payee[]; suppliers: Payee[] }>(keys.payees, { employees: [], suppliers: [] })

  const [category, setCategory] = useState<(typeof PAYOUT_CATEGORIES)[number]['id']>('EMPLOYEE')
  const [partyId, setPartyId] = useState('')
  const [amount, setAmount] = useState('')
  const [memo, setMemo] = useState('')

  const people = category === 'EMPLOYEE' ? options.employees : category === 'SUPPLIER' ? options.suppliers : []
  const needsParty = category === 'EMPLOYEE' || category === 'SUPPLIER'
  const value = toNumber(amount) ?? 0
  const label = PAYOUT_CATEGORIES.find((c) => c.id === category)!.label
  const who = people.find((p) => p.id === partyId)?.name

  const ready = value > 0 && (!needsParty || partyId !== '') && (category !== 'OTHER' || memo.trim() !== '')

  const save = () => {
    enqueue({
      kind: 'PAYOUT',
      userId,
      payload: {
        session: shift.serverId ? { serverId: shift.serverId } : { clientId: shift.clientId },
        category,
        amount: value,
        partyId: needsParty ? partyId : null,
        memo: memo.trim() || null,
      },
      summary: `مصروف ${formatMoney(value)} — ${label}${who ? ` — ${who}` : ''}`,
      cash: -value,
    })

    onClose()
  }

  return (
    <Sheet title="مصروفٌ من الدرج" onClose={onClose}>
      <div className="mx-auto grid max-w-md gap-3">
        <div className="grid grid-cols-3 gap-2">
          {PAYOUT_CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { setCategory(c.id); setPartyId('') }}
              className={`min-h-12 rounded-lg border text-sm font-semibold ${
                category === c.id ? 'border-brand bg-brand/10 text-brand' : 'border-line bg-surface'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {needsParty && (
          <div className="grid grid-cols-2 gap-2">
            {people.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPartyId(p.id)}
                className={`min-h-12 rounded-lg border text-sm ${
                  partyId === p.id ? 'border-brand bg-brand/10 font-semibold text-brand' : 'border-line bg-surface'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}

        <div className="tabular rounded-lg border border-line bg-surface px-3 py-2 text-left text-2xl font-bold" dir="ltr">
          {amount || '0'}
        </div>
        <Keypad value={amount} onChange={setAmount} />

        <input
          value={memo}
          onChange={(event) => setMemo(event.target.value)}
          placeholder={category === 'OTHER' ? 'ما هو؟ (إلزاميّ)' : 'ملاحظة (اختياريّ)'}
          className="min-h-12 rounded-lg border border-line bg-surface px-3"
        />

        <TouchButton tone="primary" wide disabled={!ready} onClick={save}>
          أخرج {formatMoney(value)}
        </TouchButton>
      </div>
    </Sheet>
  )
}

// ── الدوام ──────────────────────────────────────────────────────────────────

function PunchSheet({ userId, onClose }: { userId: string; onClose: () => void }) {
  const employees = read<Payee[]>(keys.employees, [])
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Amman' }).format(new Date())
  const punchesKey = `punches:${day}`

  useSyncExternalStore(subscribe, () => localStorage.getItem(`pos:offline:${punchesKey}`) ?? '')
  const punches = read<Record<string, 'IN' | 'OUT'>>(punchesKey, {})

  const punch = (person: Payee, way: 'IN' | 'OUT') => {
    enqueue({
      kind: 'PUNCH',
      userId,
      payload: { userId: person.id, way },
      summary: `${person.name} ${way === 'IN' ? 'وصل' : 'روّح'}`,
    })

    write(punchesKey, { ...punches, [person.id]: way })
  }

  return (
    <Sheet title="الدوامُ اليوم" onClose={onClose}>
      <ul className="mx-auto grid max-w-md gap-2">
        {employees.length === 0 && <Empty>لا موظّفين في النسخة.</Empty>}
        {employees.map((person) => {
          const inside = punches[person.id] === 'IN'

          return (
            <li key={person.id} className="flex items-center gap-2 rounded-xl border border-line p-2">
              <span className="min-w-0 flex-1 truncate font-semibold">{person.name}</span>
              {inside ? (
                <TouchButton onClick={() => punch(person, 'OUT')}>روّحت</TouchButton>
              ) : (
                <TouchButton tone="ok" onClick={() => punch(person, 'IN')}>وصلت</TouchButton>
              )}
            </li>
          )
        })}
      </ul>
    </Sheet>
  )
}

// ── الإغلاق ─────────────────────────────────────────────────────────────────

function CloseSheet({ shift, userId, onClose }: { shift: LocalShift; userId: string; onClose: () => void }) {
  const { pending } = useQueue()
  const [counted, setCounted] = useState('')

  // ⚠ **المتوقَّعُ هنا تقريبيّ**: ما يعرفه الجهازُ وحدَه — الافتتاحيُّ وما جرى
  //   فاصلًا. وما بِيع متّصلًا قبل الانقطاع يعرفه الخادم، والفرقُ الحقيقيُّ
  //   يُحسب هناك حين يُرحَّل الإغلاق.
  const offlineCash = pending
    .filter((op) => op.userId === userId)
    .reduce((sum, op) => sum + op.cash, 0)

  const close = () => {
    const value = toNumber(counted) ?? 0

    enqueue({
      kind: 'SHIFT_CLOSE',
      userId,
      payload: {
        session: shift.serverId ? { serverId: shift.serverId } : { clientId: shift.clientId },
        countedCash: value,
      },
      summary: `إغلاقُ الورديّة — عُدّ ${formatMoney(value)}`,
    })

    write(keys.shift, null)
    onClose()
  }

  return (
    <Sheet title="إغلاقُ الورديّة" onClose={onClose}>
      <div className="mx-auto grid max-w-sm gap-3">
        <Banner tone="info">
          ما جرى نقدًا وهو فاصل: <b className="tabular" dir="ltr">{formatMoney(offlineCash)}</b>
          {shift.clientId && <> · والافتتاحيّ <b className="tabular" dir="ltr">{formatMoney(shift.openingCash)}</b></>}
          . الفرقُ الحقيقيُّ يُحسب حين يرجع النت.
        </Banner>
        <p className="text-sm text-ink-soft">كم عددتَ في الدرج؟</p>
        <div className="tabular rounded-lg border border-line bg-surface px-3 py-2 text-left text-2xl font-bold" dir="ltr">
          {counted || '0'}
        </div>
        <Keypad value={counted} onChange={setCounted} />
        <TouchButton tone="primary" wide onClick={close}>أغلق الورديّة</TouchButton>
      </div>
    </Sheet>
  )
}

// ── ما ينتظر ────────────────────────────────────────────────────────────────

export function QueueSheet({ onClose }: { onClose: () => void }) {
  const { pending, failed } = useQueue()

  return (
    <Sheet title="عمليّاتٌ على الجهاز" onClose={onClose}>
      <div className="mx-auto grid max-w-md gap-4">
        <section>
          <h3 className="mb-2 text-sm font-semibold">تنتظر الترحيل ({pending.length})</h3>
          {pending.length === 0 ? (
            <Empty>لا شيء ينتظر.</Empty>
          ) : (
            <ul className="grid gap-1 text-sm">
              {pending.map((op) => (
                <li key={op.clientId} className="flex justify-between gap-2 rounded-lg border border-line px-3 py-2">
                  <span className="truncate">{op.summary}</span>
                  <span className="tabular shrink-0 text-xs text-ink-soft" dir="ltr">
                    {new Date(op.occurredAt).toLocaleTimeString('ar-JO-u-nu-latn', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {failed.length > 0 && (
          <section>
            <h3 className="mb-2 text-sm font-semibold text-danger">لم تُرحَّل ({failed.length})</h3>
            <p className="mb-2 text-xs text-ink-soft">
              وصلت الخادمَ ورُفضت — وتظهر للمالك في لوحة الإدارة ليعالجها.
            </p>
            <ul className="grid gap-1 text-sm">
              {failed.map((op) => (
                <li key={op.clientId} className="rounded-lg border border-danger/40 px-3 py-2">
                  <p className="truncate">{op.summary}</p>
                  <p className="text-xs text-danger">{op.error ?? op.errorCode}</p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Sheet>
  )
}
